import type PlanVariant from './PlanVariant'
import type { PlanTypeEnum, VariantsEnum } from './types'
import { BUSINESS_VARIANT } from './utils'

/**
 * A plan is a group of plan variants, each variant can have different pricing and discounts
 */
export default class Plan {
  private _variants: PlanVariant[]
  private _activeVariant: VariantsEnum
  private _isSelected: boolean

  public constructor(variant: PlanVariant) {
    this._variants = [variant]
    this._activeVariant = variant.type
    this._isSelected = false
  }

  // SELECTION LOGIC
  select() {
    this._isSelected = true
  }

  unselect() {
    this._isSelected = false
  }

  get isSelected() {
    return this._isSelected
  }

  toggleSelection() {
    if (this.isSelected) this.unselect()
    else this.select()
  }

  // VARIANT LOGIC
  get variantTypes() {
    return this._variants.map((variant) => variant.type)
  }

  hasVariant(variant: VariantsEnum) {
    return this.variantTypes.includes(variant)
  }

  private _getVariant(): PlanVariant {
    return this._variants.find((variant) => variant.type === this._activeVariant) as PlanVariant
  }

  addVariant(variant: PlanVariant) {
    this._variants.push(variant)
    // By default, Business variant will be active
    if (variant.type === BUSINESS_VARIANT) this._activeVariant = variant.type
  }

  setActiveVariant(variant: VariantsEnum) {
    if (this.variantTypes.includes(variant)) {
      this._activeVariant = variant
    }
  }

  setPlanType(planType: PlanTypeEnum) {
    this._getVariant().planType = planType
  }

  get internalTitle() {
    return this._getVariant().id
  }

  getFeatures(variant: VariantsEnum) {
    return this._variants.find((v) => v.type === variant)?.features || []
  }

  get title() {
    return this._getVariant().title
  }

  get icon() {
    return this._getVariant().icon
  }

  get description() {
    return this._getVariant().description
  }

  get segmentationOption() {
    return this._getVariant().segmentationOption
  }

  get pricing() {
    return this._getVariant().pricing
  }

  get activeVariantType() {
    if (this._isSelected) return this._getVariant().type
  }

  get planType() {
    return this._getVariant().planType
  }

  get includedAddons() {
    return this._getVariant().includedAddons
  }

  get includedExtraAddons() {
    return this._getVariant().includedExtraAddons
  }
}
